import React, { useCallback, useState, useRef, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  FiVideo, 
  FiX, 
  FiCheck,
  FiAlertCircle
} from 'react-icons/fi'
import { useMedia } from '../../hooks/useMedia'
import { UploadProgress, MediaFile } from '../../types/media'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../../config/firebase'

interface VideoUploadProps {
  onUploadComplete?: (videoUrl: string) => void
  maxFiles?: number
  folder?: string
}

const VideoUpload: React.FC<VideoUploadProps> = ({ 
  onUploadComplete, 
  maxFiles = 1
}) => {
  const { uploadFile } = useMedia()
  const [uploads, setUploads] = useState<UploadProgress[]>([])
  const [compressionProgress, setCompressionProgress] = useState<number>(0)
  const [uploadedMediaId, setUploadedMediaId] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Monitor compression progress from Firestore
  useEffect(() => {
    if (!uploadedMediaId) return

    const mediaDocRef = doc(db, 'media', uploadedMediaId)
    const compressionTimeout = setTimeout(() => {
      // After 10 minutes, if still processing, show timeout message
      setUploads(prevUploads => prevUploads.map(upload => 
        upload.mediaFile?.id === uploadedMediaId && upload.status === 'processing'
          ? { 
              ...upload, 
              status: 'error' as const, 
              error: 'Compression is taking longer than expected. The video may still process in the background. Check back in a few minutes.' 
            }
          : upload
      ))
      setUploadedMediaId(null)
    }, 600000) // 10 minutes

    const unsubscribe = onSnapshot(mediaDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data() as MediaFile
        
        if (data.compressionStatus === 'processing') {
          const progress = data.compressionProgress || 0
          if (progress > 0) {
            console.log(`🔄 Optimization progress: ${progress}%`)
          }
          setCompressionProgress(progress)
          setUploads(prevUploads => prevUploads.map(upload => 
            upload.mediaFile?.id === uploadedMediaId
              ? { ...upload, status: 'processing' as const, progress }
              : upload
          ))
        } else if (data.compressionStatus === 'completed') {
          console.log('✅ Video optimization complete!')
          console.log(`📦 Final size: ${(data.size / 1024 / 1024).toFixed(2)} MB`)
          if (data.compressionRatio) {
            console.log(`💾 Saved ${data.compressionRatio}% of original file size`)
          }
          clearTimeout(compressionTimeout)
          setUploads(prevUploads => prevUploads.map(upload => 
            upload.mediaFile?.id === uploadedMediaId
              ? { 
                  ...upload, 
                  status: 'completed' as const, 
                  progress: 100,
                  mediaFile: { ...upload.mediaFile, ...data } as MediaFile
                }
              : upload
          ))
          if (onUploadComplete && data.url) {
            onUploadComplete(data.url)
          }
          setUploadedMediaId(null)
          setCompressionProgress(0)
        } else if (data.compressionStatus === 'error') {
          console.error('❌ Optimization failed:', data.compressionError)
          clearTimeout(compressionTimeout)
          setUploads(prevUploads => prevUploads.map(upload => 
            upload.mediaFile?.id === uploadedMediaId
              ? { ...upload, status: 'error' as const, error: data.compressionError || 'Compression failed' }
              : upload
          ))
          setUploadedMediaId(null)
          setCompressionProgress(0)
        }
      }
    })

    return () => {
      unsubscribe()
      clearTimeout(compressionTimeout)
    }
  }, [uploadedMediaId, onUploadComplete])


  const getVideoDimensions = (file: File): Promise<{ width: number; height: number; duration: number }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.onloadedmetadata = () => {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration
        })
      }
      video.onerror = reject
      video.src = URL.createObjectURL(file)
    })
  }

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0] // Only handle one video at a time
    if (!file) return

    // Initialize upload progress
    const newUpload: UploadProgress = {
      file,
      progress: 0,
      status: 'pending'
    }

    setUploads([newUpload])

    try {
      // Step 1: Analyzing video
      console.log('📊 Step 1/3: Analyzing video...')
      setUploads([{ ...newUpload, status: 'processing', progress: 10 }])

      // Get video dimensions and duration
      const videoDimensions = await getVideoDimensions(file)
      console.log('✅ Video analyzed:', videoDimensions)

      // Step 2: Uploading to cloud
      console.log('📤 Step 2/3: Uploading to cloud storage...')
      setUploads([{ ...newUpload, status: 'uploading', progress: 0 }])

      // Upload to Firebase Storage
      const mediaFile = await uploadFile(file, (progress) => {
        console.log(`📤 Upload progress: ${progress}%`)
        setUploads([{ ...newUpload, status: 'uploading', progress }])
      })

      if (mediaFile) {
        // Step 3: Compression starting
        console.log('🔄 Step 3/3: Upload complete! Starting cloud compression...')
        console.log('⏳ This may take 2-5 minutes depending on video size')
        
        // Set the media ID to monitor compression progress
        setUploadedMediaId(mediaFile.id)
        
        // Update status to processing (waiting for compression)
        setUploads([{ 
          ...newUpload, 
          status: 'processing', 
          progress: 0, 
          mediaFile: {
            ...mediaFile,
            duration: videoDimensions.duration,
            dimensions: {
              width: videoDimensions.width,
              height: videoDimensions.height
            }
          }
        }])
        
        // Don't call onUploadComplete yet - wait for compression to complete
        // The useEffect hook will call it when compression is done
      } else {
        throw new Error('Upload failed')
      }
    } catch (error) {
      console.error('❌ Upload failed:', error)
      setUploads([{ 
        ...newUpload, 
        status: 'error', 
        error: error instanceof Error ? error.message : 'Upload failed' 
      }])
    }
  }, [uploadFile, onUploadComplete])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.webm', '.mov', '.avi']
    },
    maxFiles,
    maxSize: 1024 * 1024 * 1024, // 1GB - cloud function will compress before making available
  })

  const removeUpload = () => {
    setUploads([])
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }


  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragActive
            ? 'border-primary bg-primary/5 scale-105'
            : 'border-neutral-300 hover:border-primary hover:bg-neutral-50'
        }`}
      >
        <input {...getInputProps()} />
        
        <motion.div
          animate={isDragActive ? { scale: 1.1 } : { scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          <FiVideo size={48} className={`mx-auto mb-4 ${isDragActive ? 'text-primary' : 'text-neutral-400'}`} />
          
          {isDragActive ? (
            <div>
              <p className="text-lg font-semibold text-primary mb-2">Drop video here!</p>
              <p className="text-neutral-600">Release to upload your background video</p>
            </div>
          ) : (
            <div>
              <p className="text-lg font-semibold text-neutral-800 mb-2">
                Drag & drop background video here
              </p>
              <p className="text-neutral-600 mb-4">
                or <span className="text-primary font-medium">click to browse</span>
              </p>
              <div className="flex items-center justify-center space-x-4 text-sm text-neutral-500">
                <span>Max 1GB</span>
                <span>•</span>
                <span>MP4, WebM, MOV</span>
                <span>•</span>
                <span>Auto-optimized for web</span>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Upload Progress */}
      <AnimatePresence>
        {uploads.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-200"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                Video Upload Progress
              </h3>
            </div>

            {uploads.map((upload, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center space-x-4 p-4 bg-neutral-50 rounded-xl"
              >
                {/* Video Icon */}
                <div className={`p-2 rounded-lg ${
                  upload.status === 'completed' ? 'bg-green-100' :
                  upload.status === 'error' ? 'bg-red-100' :
                  upload.status === 'processing' ? 'bg-blue-100' :
                  'bg-neutral-200'
                }`}>
                {upload.status === 'completed' ? (
                  <FiCheck className="text-green-600" size={20} />
                ) : upload.status === 'error' ? (
                  <FiAlertCircle className="text-red-600" size={20} />
                ) : upload.status === 'processing' ? (
                  <div className="loading-spinner w-5 h-5 border-2 border-blue-600"></div>
                ) : (
                  <FiVideo className="text-neutral-600" size={20} />
                )}
                </div>

                {/* File Info */}
                  <div className="flex-1 min-w-0">
                  <p className="font-medium text-neutral-800 truncate mb-1">
                    {upload.file.name}
                  </p>
                  
                  {/* Status Steps */}
                  <div className="flex items-center space-x-2 text-xs mb-2">
                    <div className="flex items-center space-x-1">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        upload.status !== 'pending' ? 'bg-green-500 text-white' : 'bg-neutral-300'
                      }`}>
                        {upload.status !== 'pending' ? '✓' : '1'}
                      </div>
                      <span className={upload.status !== 'pending' ? 'text-green-600 font-medium' : 'text-neutral-500'}>
                        Upload
                      </span>
                    </div>
                    
                    <div className={`w-6 h-px ${upload.status === 'processing' || upload.status === 'completed' ? 'bg-green-500' : 'bg-neutral-300'}`}></div>
                    
                    <div className="flex items-center space-x-1">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        upload.status === 'completed' ? 'bg-green-500 text-white' : 
                        upload.status === 'processing' ? 'bg-blue-500 text-white animate-pulse' : 
                        'bg-neutral-300'
                      }`}>
                        {upload.status === 'completed' ? '✓' : '2'}
                      </div>
                      <span className={
                        upload.status === 'completed' ? 'text-green-600 font-medium' : 
                        upload.status === 'processing' ? 'text-blue-600 font-medium' : 
                        'text-neutral-500'
                      }>
                        Optimize
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-sm text-neutral-600">
                    <span>{formatFileSize(upload.file.size)}</span>
                    {upload.status === 'uploading' && (
                      <>
                        <span> • </span>
                        <span className="text-blue-600 font-medium">Uploading to cloud...</span>
                      </>
                    )}
                    {upload.status === 'processing' && (
                      <>
                        <span> • </span>
                        <span className="text-blue-600 font-medium">
                          {compressionProgress > 0 
                            ? `Optimizing: ${compressionProgress}%`
                            : 'Starting optimization...'}
                        </span>
                      </>
                    )}
                    {upload.status === 'error' && upload.error && (
                      <>
                        <span> • </span>
                        <span className="text-red-600">{upload.error}</span>
                      </>
                    )}
                    {upload.status === 'completed' && upload.mediaFile && (
                      <>
                        <span> • </span>
                        <span className="text-green-600 font-medium">
                          ✓ Ready! {upload.mediaFile.compressionRatio 
                            ? `(${upload.mediaFile.compressionRatio}% smaller)`
                            : ''}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {(upload.status === 'uploading' || upload.status === 'processing') && (
                    <div className="mt-2">
                      <div className="w-full bg-neutral-200 rounded-full h-2">
                        <motion.div
                          className={`h-2 rounded-full ${
                            upload.status === 'processing' ? 'bg-blue-500' : 'bg-primary'
                          }`}
                          initial={{ width: 0 }}
                          animate={{ 
                            width: upload.status === 'processing' 
                              ? `${compressionProgress}%` 
                              : `${upload.progress}%` 
                          }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">
                        {upload.status === 'processing' 
                          ? compressionProgress > 0
                            ? `Step 2/2: Optimizing video... ${compressionProgress}% complete`
                            : 'Step 2/2: Optimization starting... (2-5 minutes)'
                          : `Step 1/2: Uploading to cloud... ${upload.progress}%`
                        }
                      </p>
                    </div>
                  )}

                  {/* Video Preview */}
                  {upload.status === 'completed' && upload.mediaFile && (
                    <div className="mt-3">
                      <video
                        ref={videoRef}
                        src={upload.mediaFile.url}
                        className="w-full max-w-md h-32 object-cover rounded-lg border border-neutral-200"
                        controls
                        muted
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}
                </div>

                {/* Remove Button */}
                <button
                  onClick={removeUpload}
                  className="p-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FiX size={16} />
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default VideoUpload
